import { Node, mergeAttributes, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { parseCardSyntax, type ParsedCard } from '../cardParser';

type CardAttrs = {
  cardId: string;
  cardType: 'basic' | 'cloze' | 'mcq';
  front: string;
  back: string;
  options: string[];
  correct: number[];
  explanation: string;
};

export const CardBlock = Node.create({
  name: 'cardBlock',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,
  isolating: true,

  addAttributes() {
    return {
      cardId: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-card-id'),
        renderHTML: (attrs) => (attrs.cardId ? { 'data-card-id': attrs.cardId } : {}),
      },
      cardType: {
        default: 'basic',
        parseHTML: (el) => el.getAttribute('data-card-type') ?? 'basic',
        renderHTML: (attrs) => ({ 'data-card-type': attrs.cardType }),
      },
      front: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-front') ?? '',
        renderHTML: (attrs) => ({ 'data-front': attrs.front }),
      },
      back: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-back') ?? '',
        renderHTML: (attrs) => ({ 'data-back': attrs.back }),
      },
      options: {
        default: [] as string[],
        parseHTML: (el) => JSON.parse(el.getAttribute('data-options') ?? '[]'),
        renderHTML: (attrs) => ({ 'data-options': JSON.stringify(attrs.options ?? []) }),
      },
      correct: {
        default: [] as number[],
        parseHTML: (el) => JSON.parse(el.getAttribute('data-correct') ?? '[]'),
        renderHTML: (attrs) => ({ 'data-correct': JSON.stringify(attrs.correct ?? []) }),
      },
      explanation: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-explanation') ?? '',
        renderHTML: (attrs) =>
          attrs.explanation ? { 'data-explanation': attrs.explanation } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-card-block]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-card-block': '', class: 'card-block' })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(CardBlockView);
  },

  addProseMirrorPlugins() {
    return [cardTransformPlugin];
  },
});

// --- Transform plugin: on Enter at the end of a paragraph, convert it to a card block if syntax matches.

const cardTransformKey = new PluginKey('cardTransform');

const cardTransformPlugin = new Plugin({
  key: cardTransformKey,
  props: {
    handleKeyDown(view, event) {
      if (event.key !== 'Enter' || event.shiftKey) return false;
      const { state } = view;
      const { $from, empty } = state.selection;
      if (!empty) return false;
      const parent = $from.parent;
      if (parent.type.name !== 'paragraph') return false;
      if ($from.parentOffset !== parent.nodeSize - 2) return false;
      const text = parent.textContent;
      if (!text) return false;
      const parsed = parseCardSyntax(text);
      if (!parsed) return false;

      const attrs = toCardAttrs(parsed);
      const cardBlock = state.schema.nodes.cardBlock!.create(attrs);
      const emptyParagraph = state.schema.nodes.paragraph!.create();

      const start = $from.before();
      const end = $from.after();
      const tr = state.tr.replaceWith(start, end, [cardBlock, emptyParagraph]);
      const afterPos = start + cardBlock.nodeSize + 1;
      tr.setSelection(TextSelection.near(tr.doc.resolve(afterPos)));
      tr.scrollIntoView();
      view.dispatch(tr);
      return true;
    },
  },
});

function toCardAttrs(parsed: ParsedCard): CardAttrs {
  const base = {
    cardId: crypto.randomUUID(),
    front: '',
    back: '',
    options: [] as string[],
    correct: [] as number[],
    explanation: '',
  };
  if (parsed.cardType === 'mcq') {
    return {
      ...base,
      cardType: 'mcq',
      front: parsed.front,
      back: parsed.back,
      options: parsed.options,
      correct: parsed.correct,
    };
  }
  if (parsed.cardType === 'cloze') {
    return { ...base, cardType: 'cloze', front: parsed.front, back: '' };
  }
  return { ...base, cardType: 'basic', front: parsed.front, back: parsed.back };
}

// Serialize back to the inline syntax so a user editing the block edits plain text.
function serializeCard(attrs: CardAttrs): string {
  if (attrs.cardType === 'basic') {
    return attrs.back ? `${attrs.front} -> ${attrs.back}` : attrs.front;
  }
  if (attrs.cardType === 'cloze') {
    return attrs.front;
  }
  const correctIdx = attrs.correct[0] ?? 0;
  const correctText = attrs.options[correctIdx] ?? attrs.back;
  const distractors = attrs.options.filter((_, i) => i !== correctIdx);
  return `${attrs.front} -> ${correctText} [${distractors.join(', ')}]`;
}

// --- NodeView

function CardBlockView({ node, getPos, editor, selected }: NodeViewProps) {
  const attrs = node.attrs as CardAttrs;

  const openForEdit = () => {
    if (!editor.isEditable) return;
    const pos = typeof getPos === 'function' ? getPos() : null;
    if (pos == null) return;
    const text = serializeCard(attrs);
    const schema = editor.schema;
    const paragraph = schema.nodes.paragraph!.create(
      null,
      text ? schema.text(text) : null,
    );
    const tr = editor.state.tr.replaceWith(pos, pos + node.nodeSize, paragraph);
    tr.setSelection(TextSelection.near(tr.doc.resolve(pos + paragraph.nodeSize - 1)));
    editor.view.dispatch(tr);
    editor.view.focus();
  };

  return (
    <NodeViewWrapper
      className="card-block"
      data-selected={selected ? 'true' : undefined}
      data-card-type={attrs.cardType}
      onDoubleClick={openForEdit}
    >
      <CompactView attrs={attrs} />
    </NodeViewWrapper>
  );
}

function CompactView({ attrs }: { attrs: CardAttrs }) {
  if (attrs.cardType === 'basic') {
    return (
      <div className="card-block-compact-basic">
        <span className="card-block-compact-front">{attrs.front || <em>empty</em>}</span>
        <span className="card-block-compact-arrow">→</span>
        <span className="card-block-compact-back">{attrs.back || <em>empty</em>}</span>
      </div>
    );
  }
  if (attrs.cardType === 'cloze') {
    return (
      <div className="card-block-compact-cloze">
        {attrs.front ? renderCloze(attrs.front) : <em>empty</em>}
      </div>
    );
  }
  const correctSet = new Set(attrs.correct);
  return (
    <div className="card-block-compact-mcq">
      <div className="card-block-compact-question">{attrs.front || <em>empty</em>}</div>
      <ul className="card-block-compact-options">
        {attrs.options.map((option, i) => (
          <li key={i} className={correctSet.has(i) ? 'correct' : undefined}>
            <span className="marker">{correctSet.has(i) ? '✓' : '·'}</span>
            <span className="label">{option || <em>empty</em>}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function renderCloze(text: string) {
  const parts = text.split(/(\{\{c\d+::[^}]+\}\})/g);
  return parts.map((p, i) => {
    const m = p.match(/^\{\{c(\d+)::([^}]+)\}\}$/);
    if (!m) return <span key={i}>{p}</span>;
    return (
      <span key={i} className="cloze-inline">
        {m[2]}
      </span>
    );
  });
}
